import {
  ApolloClient,
  ApolloLink,
  DocumentNode,
  InMemoryCache,
  OperationVariables,
} from "@apollo/client";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { Environment } from "./environment/features/environment";
import { onError } from "@apollo/client/link/error";
import { resetSessionStorage } from "../root/auth/infrastructure/slices/authSlice/authSlice";

type Query = <QV extends OperationVariables | undefined, RT>(
  name: string,
  query: DocumentNode,
  variables?: QV,
) => Promise<RT>;
type Mutate = <MV extends OperationVariables | undefined, RT>(
  name: string,
  mutation: DocumentNode,
  variables?: MV,
) => Promise<RT>;

export type GraphQLClient = {
  query: Query;
  mutate: Mutate;
};

export interface GraphQLErrorMessage {
  message: string;
}

export const createGQLClient = (): GraphQLClient => {
  const cache = new InMemoryCache({
    addTypename: false,
    resultCaching: false,
  });

  // Middleware to add the Authorization header
  const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem("token");
    operation.setContext({
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
    return forward(operation);
  });

  // Error handling link
  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.message === "Unauthenticated.") {
          resetSessionStorage();
        }
      }
    }
  });

  const uploadLink = createUploadLink({
    uri: `${Environment.API_BASE_URL}/graphql`,
  });

  // Combine the authLink and the uploadLink
  const link = ApolloLink.from([authLink, errorLink, uploadLink]);

  const client = new ApolloClient({
    // Provide required constructor fields
    cache: cache,
    link: link,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "no-cache",
      },
      mutate: {
        fetchPolicy: "no-cache",
      },
    },
  });

  const query: Query = (name, query, variables) => {
    return client
      .query({
        query,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(({ data }) => data[name]);
  };

  const mutate: Mutate = (name, mutation, variables) => {
    return client
      .mutate({
        mutation,
        variables,
      })
      .then(({ data }) => data[name]);
  };

  return { query, mutate };
};
